/**
 * ขอเปลี่ยนกะการทำงาน (มุมพนักงาน) — เปลี่ยนกะของตัวเองในวันหนึ่ง
 *   เช่น วันนี้เข้ากะ 08:00 → ขอเปลี่ยนเป็นเข้ากะ 12:00 (ไม่ได้แลกกับใคร)
 * ปกติขอล่วงหน้าอย่างน้อย 1 วัน · ถ้ากระชั้นกว่านั้น = กระทันหัน (ต้องมีเหตุผล, ให้ HR อนุมัติ)
 * อนุมัติ 2 ชั้น (หัวหน้า → HR) แล้วเขียนทับกะของวันนั้น
 */
import { useState } from 'react';
import { Repeat, Send, ArrowRight, AlertTriangle } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyShiftChanges, useCreateChangeShift, useShiftOptions, type ShiftChangeRow } from '../hooks';
import { useToast } from '../components/ui/toast';
import { todayISO, toISODate } from '../lib/thai-date';

const STATUS: Record<ShiftChangeRow['status'], { label: string; cls: string }> = {
  pending: { label: 'รอหัวหน้า/HR อนุมัติ', cls: 'badge-warn' },
  approved: { label: 'อนุมัติแล้ว', cls: 'badge-success' },
  rejected: { label: 'ไม่อนุมัติ', cls: 'badge-danger' },
  cancelled: { label: 'ยกเลิก', cls: 'badge-danger' },
};

function fmtThai(iso: string): string {
  const d = new Date(`${iso}T00:00:00+07:00`);
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Bangkok' }).format(d);
}

// วันนี้/พรุ่งนี้ (ICT) แบบ YYYY-MM-DD
function todayThai(): string {
  return todayISO();
}
function tomorrowThai(): string {
  return toISODate(new Date(Date.now() + 86400_000));
}

export function ChangeShift() {
  const mine = useMyShiftChanges();
  const create = useCreateChangeShift();
  const { data: shiftOptions } = useShiftOptions();
  const toast = useToast();

  const minDate = todayThai(); // เลือกตั้งแต่วันนี้ (กระทันหันได้ แต่ห้ามย้อนหลัง)
  const tomorrow = tomorrowThai();
  const [date, setDate] = useState('');
  const [targetShiftId, setTargetShiftId] = useState('');
  const [reason, setReason] = useState('');

  // กระทันหัน = แจ้งไม่ครบล่วงหน้า 1 วัน (วันที่ขอเป็นวันนี้)
  const isUrgent = !!date && date < tomorrow;

  const shifts = shiftOptions ?? [];
  const nameOf = (id: string | null) => {
    const s = shifts.find((x) => x.id === id);
    return s ? `${s.name} (${s.start_time}–${s.end_time})` : null;
  };

  function submit() {
    if (!date) return toast.error('เลือกวันที่ต้องการเปลี่ยนกะ');
    if (!targetShiftId) return toast.error('เลือกกะใหม่ที่จะเข้าทำงาน');
    if (isUrgent && !reason.trim()) return toast.error('เปลี่ยนกะกระทันหัน ต้องระบุเหตุผล');
    create.mutate(
      { date, target_shift_id: targetShiftId, reason: reason || undefined },
      {
        onSuccess: () => {
          toast.success(isUrgent ? 'ส่งคำขอกระทันหันแล้ว — รอ HR อนุมัติ' : 'ส่งคำขอแล้ว — รอหัวหน้า/HR อนุมัติ');
          setDate(''); setTargetShiftId(''); setReason('');
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'ส่งคำขอไม่สำเร็จ'),
      },
    );
  }

  // แสดงเฉพาะคำขอชนิด "เปลี่ยนกะ"
  const rows = (mine.data ?? []).filter((r) => r.request_type === 'shift_change');

  return (
    <PageLayout title="ขอเปลี่ยนกะการทำงาน">
      <div className="space-y-5 pb-10">
        {/* สร้างคำขอ */}
        <div className="card p-4 space-y-3">
          <div className="font-medium text-ink flex items-center gap-1.5">
            <Repeat className="w-4 h-4 text-teal" /> ขอเปลี่ยนกะการทำงาน
          </div>
          <p className="text-xs text-gray-500">
            ขอเปลี่ยน <b>กะของตัวเอง</b> ในวันที่เลือก เช่น เข้างาน 08:00 → เข้างาน 12:00.
            ปกติขอ <b>ล่วงหน้าอย่างน้อย 1 วัน</b> · ถ้ากระชั้นกว่านั้น = <b>กระทันหัน</b> (ต้องมีเหตุผล, ให้ HR อนุมัติ).
          </p>
          <div>
            <label className="label">วันที่ต้องการเปลี่ยนกะ</label>
            <input
              type="date" value={date} min={minDate}
              onChange={(e) => setDate(e.target.value)} className="input w-full"
            />
          </div>
          <div>
            <label className="label">กะใหม่ที่จะเข้าทำงาน</label>
            <select value={targetShiftId} onChange={(e) => setTargetShiftId(e.target.value)} className="input w-full">
              <option value="">— เลือกกะใหม่ —</option>
              {shifts.map((s) => (
                <option key={s.id} value={s.id}>{s.name} ({s.start_time}–{s.end_time})</option>
              ))}
            </select>
          </div>

          {isUrgent && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><b>คำขอกระทันหัน</b> (แจ้งไม่ครบล่วงหน้า 1 วัน) — ต้องระบุเหตุผล และจะส่งให้ <b>HR</b> อนุมัติเท่านั้น</span>
            </div>
          )}

          <div>
            <label className="label">เหตุผล{isUrgent ? ' (จำเป็นสำหรับคำขอกระทันหัน)' : ' (ถ้ามี)'}</label>
            <input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น มีนัดช่วงเช้า ขอเข้ากะบ่าย" className={`input w-full ${isUrgent && !reason.trim() ? 'border-amber-400' : ''}`} maxLength={300}
            />
          </div>
          <button
            onClick={submit}
            disabled={create.isPending || !date || !targetShiftId || (isUrgent && !reason.trim())}
            className="btn-cta w-full justify-center flex items-center gap-2"
          >
            <Send className="w-4 h-4" /> {create.isPending ? 'กำลังส่ง…' : isUrgent ? 'ส่งคำขอกระทันหัน' : 'ส่งคำขอ'}
          </button>
        </div>

        {/* คำขอของฉัน */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">คำขอของฉัน</div>
          {mine.isLoading ? (
            <p className="text-sm text-gray-400">กำลังโหลด…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-400">ยังไม่มีคำขอ</p>
          ) : (
            rows.map((r) => {
              const from = nameOf(r.original_shift_id);
              const to = nameOf(r.target_shift_id);
              return (
                <div key={r.id} className="card p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-ink">{fmtThai(r.original_date)}</div>
                    <div className="flex items-center gap-1 shrink-0">
                      {r.is_urgent && <span className="badge-warn">กระทันหัน</span>}
                      <span className={STATUS[r.status].cls}>{STATUS[r.status].label}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 flex items-center gap-1.5 flex-wrap">
                    <span>{from ?? 'กะเดิม'}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                    <span className="font-medium text-ink">{to ?? 'กะใหม่'}</span>
                  </div>
                  {r.reason && <p className="text-xs text-gray-500">เหตุผล: {r.reason}</p>}
                  {r.status === 'rejected' && r.rejection_reason && (
                    <p className="text-xs text-red-600">เหตุผลที่ไม่อนุมัติ: {r.rejection_reason}</p>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </PageLayout>
  );
}
